export type View =
  | "overview"
  | "work"
  | "pr"
  | "issue"
  | "insights"
  | "quality"
  | "settings"
  | "requests"
  | "request"
  | "status";
export type WorkTab = "pulls" | "issues" | "teams" | "merge";
/// Two windows because they answer different questions: thirty days a day at a
/// time shows a trend, twenty-four hours half an hour at a time shows what
/// happened this morning.
export type StatusWindow = "30d" | "24h";
export type InsightTab = "activity" | "completed" | "analytics" | "reviews";
/// Test quality lives under Insights: `portfolio` compares every repository,
/// `repositories` drills into one, and `ownership` looks at the people.
export type QualityTab = "portfolio" | "repositories" | "ownership";
export type SettingsTab = "general" | "repositories" | "integrations" | "runtime" | "memories" | "team";

export type AppRoute =
  | { view: "overview" }
  | { view: "requests" }
  | { view: "status"; statusWindow: StatusWindow }
  | { view: "request" }
  | { view: "work"; workTab: WorkTab }
  | { view: "insights"; insightTab: InsightTab }
  | { view: "quality"; qualityTab: QualityTab; repository?: string }
  | { view: "settings"; settingsTab: SettingsTab }
  | { view: "pr"; owner: string; repository: string; number: number }
  | { view: "issue"; owner: string; repository: string; number: number };

const statusWindows = new Set<StatusWindow>(["30d", "24h"]);
const insightTabs = new Set<InsightTab>(["activity", "completed", "analytics", "reviews"]);
const workTabs = new Set<WorkTab>(["pulls", "issues", "teams", "merge"]);
const qualityTabs = new Set<QualityTab>(["portfolio", "repositories", "ownership"]);
const settingsTabs = new Set<SettingsTab>(["general", "repositories", "integrations", "runtime", "memories", "team"]);

/// Shared with the staff portal, which serves the same status page to members
/// on the same URLs, so both shells normalize an unknown window identically.
export function statusWindowFromPath(segment: string | undefined): StatusWindow {
  return statusWindows.has(segment as StatusWindow) ? segment as StatusWindow : "30d";
}

function decode(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function parseAppRoute(pathname: string): AppRoute {
  const parts = pathname.split("/").filter(Boolean).map(decode);
  if (parts[0] === "requests") return parts[1] ? { view: "request" } : { view: "requests" };
  // Inbox is now part of Requests. Keep old bookmarks landing on the board.
  if (parts[0] === "inbox") return { view: "requests" };
  // Team administration now lives under Settings. Preserve the old URL.
  if (parts[0] === "team") return { view: "settings", settingsTab: "team" };
  if (parts[0] === "prs" && parts.length === 4) {
    const number = Number(parts[3]);
    if (parts[1] && parts[2] && Number.isInteger(number) && number > 0) {
      return { view: "pr", owner: parts[1], repository: parts[2], number };
    }
  }
  if (parts[0] === "issues" && parts.length === 4) {
    const number = Number(parts[3]);
    if (parts[1] && parts[2] && Number.isInteger(number) && number > 0) {
      return { view: "issue", owner: parts[1], repository: parts[2], number };
    }
  }
  if (parts[0] === "status") {
    // 30d is the default view. Trace retention is 72h, so most of it is grey
    // today — the page says so on its face rather than hiding the window, and
    // the grey is the argument for keeping our own rollups.
    return { view: "status", statusWindow: statusWindowFromPath(parts[1]) };
  }
  if (parts[0] === "work") {
    const tab = parts[1] as WorkTab;
    return { view: "work", workTab: workTabs.has(tab) ? tab : "pulls" };
  }
  if (parts[0] === "quality") {
    const tab = parts[1] as QualityTab;
    // Legacy quality links are parsed and normalized by the application shell.
    const repository = parts.length >= 4 ? `${parts[2]}/${parts[3]}` : undefined;
    return {
      view: "quality",
      qualityTab: qualityTabs.has(tab) ? tab : "portfolio",
      repository,
    };
  }
  if (parts[0] === "insights") {
    if (parts[1] === "quality") {
      const tab = parts[2] as QualityTab;
      const repository = parts.length >= 5 ? `${parts[3]}/${parts[4]}` : undefined;
      return {
        view: "quality",
        qualityTab: qualityTabs.has(tab) ? tab : "portfolio",
        repository,
      };
    }
    const tab = parts[1] as InsightTab;
    return { view: "insights", insightTab: insightTabs.has(tab) ? tab : "analytics" };
  }
  if (parts[0] === "settings") {
    const tab = parts[1] as SettingsTab;
    return { view: "settings", settingsTab: settingsTabs.has(tab) ? tab : "general" };
  }
  return { view: "overview" };
}

export function pullRequestPath(repository: string, number: number) {
  const [owner, name] = repository.split("/");
  return `/prs/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/${number}`;
}

export function issuePath(repository: string, number: number) {
  const [owner, name] = repository.split("/");
  return `/issues/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/${number}`;
}

export function qualityRepositoryPath(repository: string) {
  const [owner, name] = repository.split("/");
  if (!owner || !name) return "/insights/quality/repositories";
  return `/insights/quality/repositories/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`;
}
